import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Tag, Typography, Table, Input, Space, Dropdown, Button, App, Tooltip } from 'antd';
import { MoreOutlined, EditOutlined, PlusOutlined, DeleteOutlined, LockOutlined } from '@ant-design/icons';
import { deleteRegion, listRegions, updateRegionStatus, } from '../../services/regions';
import usePermissions from '../../hooks/usePermissions';

const { Title } = Typography;
const { useApp } = App;

const RegionsList = () => {
  const navigate = useNavigate();
  const { message, modal } = useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const { can } = usePermissions();

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [sorter, setSorter] = useState({});
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async (page, pageSize, srt, q) => {
    setLoading(true);
    try {
      const { items, total } = await listRegions({
        page: page || pagination.current,
        pageSize: pageSize || pagination.pageSize,
        search: q || undefined,
        sortField: srt?.field,
        sortOrder: srt?.order,
      });
      setData(items || []);
      setPagination(prev => ({ 
        ...prev, 
        current: page || prev.current, 
        pageSize: pageSize || prev.pageSize, 
        total,
      }));
    } catch (err) {
      message.error('No se pudo cargar la lista de regiones.');
    } finally {
      setLoading(false);
    }
  }, [pagination, message]);

  useEffect(() => {
    fetchData(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (pag, filters, srt) => {
    setSorter(srt || {});
    fetchData(pag.current, pag.pageSize, srt, search);
  };

  const onSearch = (value) => {
    setSearch(value || '');
    // al buscar, reinicia a página 1
    fetchData(1, pagination.pageSize, sorter, value || '');
  };

  // Función para manejar las acciones del menú
  const handleMenuAction = (action, record) => {
    switch (action) {
      case 'edit':
        navigate(`/regions/${record.id}/edit`);
        break;
      case 'toggle':
        const willActivate = record.is_deleted;
        modal.confirm({
          title: willActivate ? '¿Activar región?' : '¿Desactivar región?',
          content: `${record.description || 'región'}`,
          okText: willActivate ? 'Activar' : 'Desactivar',
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await updateRegionStatus(record.id, willActivate);
              message.success(willActivate ? 'región activada' : 'región desactivada');
              fetchData(pagination.current, pagination.pageSize, sorter, search);
            } catch {
              message.error('No se pudo actualizar el estatus');
            }
          },
        });
        break;
      case 'delete':
        modal.confirm({
          title: '¿Eliminar región?',
          content: `${record.description || 'Región'}`,
          okText: 'Eliminar',
          okButtonProps: { danger: true },
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await deleteRegion(record.id);
              message.success('Región eliminada');
              const nextPage = (data.length === 1 && pagination.current > 1)
                ? pagination.current - 1
                : pagination.current;
              setPagination(prev => ({ ...prev, current: nextPage }));
              fetchData(nextPage, pagination.pageSize, sorter, search);
            } catch(error) {
              message.error('No se pudo eliminar la región');
            }
          },
        });
        break;
      
      default:
        break;
    }
  };

  const getActionsMenu = (record) => {
    const menuItems = [
      can('regions.edit') && {
        key: 'edit',
        icon: <EditOutlined />,
        label: 'Editar Región',
      },
      can('regions.toggle') && {
        key: 'toggle',
        icon: <LockOutlined />,
        label: !record.is_deleted ? 'Desactivar' : 'Activar',
      },
      can('regions.delete') && {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Borrar región',
        danger: true,
      },
    ];

    return {
      items: menuItems,
      onClick: ({ key }) => handleMenuAction(key, record),
    };
  };

  const columns = [
    { title: 'Región', dataIndex: 'description', key: 'description', sorter: true, },
    { title: 'Estado', dataIndex: 'is_deleted', key: 'is_deleted', sorter: true,
      render: (isDeleted) => (
          <Tag color={!isDeleted ? 'green' : 'volcano'}>
            {!isDeleted ? 'Activo' : 'Inactivo'}
          </Tag>
        ),
     },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      width: 100,
      render: (_, record) => (
        <Dropdown menu={getActionsMenu(record)} trigger={['click']} placement="bottomRight">
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
            style={{ fontSize: '16px' }}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div className="header-container">
        <Title level={3} style={{ marginBottom: 0 }}>
          Regiones
        </Title>
        {can('regions.add') && (<Tooltip title="Agregar Región">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="btn-add"
            aria-label="Agregar región"
            onClick={() => navigate('/regions/add')}
          />
        </Tooltip>)}
      </div>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input.Search
            placeholder="Región"
            allowClear
            onSearch={onSearch}
            style={{ maxWidth: 360 }}
          />
          <Table
            rowKey={(r) => r.id ?? r._id}
            columns={columns}
            dataSource={data}
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
              showTotal: (total) => `Total: ${total} regiones`
            }}
            onChange={onChange}
          />
        </Space>
      </Card>
    </div>
  );
};

export default RegionsList;
